interface ParsedConfigs {
  apiURL: string
  auth0Domain: string
  auth0ClientID: string
  auth0Audience: string
  auth0Scopes: string
  environment: string
  showDeploymentInfo: boolean
  ff_cs_suggestions: boolean
  ff_simplified_seismic: boolean
  ff_non_wall_tensile: boolean
  // Enable on christmas :D
  enableSnowEasterEgg: boolean
  muiXProLicense: string | undefined
  apryseLicenceKey: string | undefined
  useNgrok: boolean
  // Base URL of the Copilot runtime for the in-app AI chat (e.g.
  // http://localhost:8120/api/copilotkit). Unset = chat disabled.
  copilotRuntimeUrl: string | undefined
}

const defaultConfig: ParsedConfigs = {
  apiURL: 'http://localhost:8100',
  auth0Domain: 'dev-diem3oe2.eu.auth0.com',
  auth0ClientID: 'az1QUs1k2bljjifGgWC85GKqy6qZSlGW',
  auth0Audience: 'api.modugen.de',
  // Part of the Auth0 SDK's localStorage cache key, which the e2e tests read the access token
  // from: keep `CYPRESS_AUTH0_KEY` in `cypress/config/.env.*` in sync when this changes.
  auth0Scopes: 'openid access:all access:admin access:ai access:dev email',
  environment: 'development',
  showDeploymentInfo: false,
  ff_cs_suggestions: false,
  ff_simplified_seismic: false,
  ff_non_wall_tensile: false,
  enableSnowEasterEgg: false,
  muiXProLicense: undefined,
  apryseLicenceKey: undefined,
  useNgrok: false,
  copilotRuntimeUrl: undefined,
}

interface EnvironmentConfigs {
  apiURL: string | undefined
  auth0Domain: string | undefined
  auth0ClientID: string | undefined
  auth0Audience: string | undefined
  auth0Scopes: string
  environment: string | undefined
  showDeploymentInfo: boolean | undefined
  ff_cs_suggestions: boolean | undefined
  ff_simplified_seismic: boolean | undefined
  ff_non_wall_tensile: boolean | undefined
  enableSnowEasterEgg: boolean | undefined
  muiXProLicense: string | undefined
  apryseLicenceKey: string | undefined
  useNgrok: boolean | undefined
  copilotRuntimeUrl: string | undefined
}

function parseBoolean(value: string | undefined): boolean | undefined {
  if (value === undefined) {
    return undefined
  }
  return value.toLowerCase() === 'true'
}

const currentEnvConfig: EnvironmentConfigs = {
  apiURL: import.meta.env.VITE_API_URL,
  auth0Domain: import.meta.env.VITE_AUTH_0_DOMAIN,
  auth0ClientID: import.meta.env.VITE_AUTH_0_CLIENT_ID,
  auth0Audience: import.meta.env.VITE_AUTH_0_AUDIENCE,
  auth0Scopes: import.meta.env.VITE_AUTH_0_SCOPES,
  environment: import.meta.env.VITE_ENVIRONMENT,
  showDeploymentInfo: parseBoolean(import.meta.env.VITE_SHOW_DEPLOYMENT_INFO),
  ff_cs_suggestions: parseBoolean(import.meta.env.VITE_FF_CS_SUGGESTIONS),
  ff_simplified_seismic: parseBoolean(import.meta.env.VITE_FF_SIMPLIFIED_SEISMIC),
  ff_non_wall_tensile: parseBoolean(import.meta.env.VITE_FF_NON_WALL_TENSILE),
  enableSnowEasterEgg: parseBoolean(import.meta.env.VITE_ENABLE_SNOW_EE),
  muiXProLicense: import.meta.env.VITE_MUI_X_PRO_LICENSE,
  apryseLicenceKey: import.meta.env.VITE_APRYSE_LICENCE_KEY,
  // See: https://app.clickup.com/2590559/v/dc/2f1uz-2781
  useNgrok: parseBoolean(import.meta.env.VITE_USE_NGROK),
  copilotRuntimeUrl: import.meta.env.VITE_COPILOT_RUNTIME_URL || undefined,
}

function mergeConfigs(
  defaultConfig: ParsedConfigs,
  currentEnvConfig: EnvironmentConfigs,
): ParsedConfigs {
  // Merges the defaults and the environment variables,
  // prefering the environment variables if they are defined

  const mergedConfig: ParsedConfigs = { ...defaultConfig }

  for (const key in defaultConfig) {
    const typedKey = key as keyof ParsedConfigs
    if (currentEnvConfig[typedKey] !== undefined) {
      // @ts-ignore
      mergedConfig[typedKey] = currentEnvConfig[typedKey]
    }
  }

  return mergedConfig
}

export const appConfig = mergeConfigs(defaultConfig, currentEnvConfig)
